import { AxiosError, InternalAxiosRequestConfig } from 'axios';

import { LoginResponse } from 'api/model';
import { AXIOS_INSTANCE } from './custom-instance';

/**
 * Token auto-refresh for the Orval/axios path.
 *
 * This replicates the behavior that the legacy NSwag `useApiCall` /
 * `ApiCallConfiguration` layer provided: when a request comes back 401, try to
 * obtain a fresh token via `ValidateRefreshToken`, then retry the original
 * request once. On failure the user is signed out.
 *
 * The axios token is kept as the single source of truth in this module (rather
 * than read from React state in a request interceptor) so that a token refreshed
 * mid-flight is applied to the retried request synchronously, before the React
 * auth state has had a chance to propagate. `useAuthenticationState` keeps this
 * module in sync via `updateAuthTokens` / `setAuthCallbacks`.
 */

let currentToken: string | null = null;
let currentRefreshToken: string | null = null;

let onTokensRefreshed:
  | ((token: string, refreshToken: string, userId: number) => void)
  | null = null;
let onRefreshFailed: (() => void) | null = null;

/** Keep the module's view of the current tokens in sync with React auth state. */
export const updateAuthTokens = (
  token: string | null | undefined,
  refreshToken: string | null | undefined
) => {
  currentToken = token ?? null;
  currentRefreshToken = refreshToken ?? null;
};

/** Wire the React state updates that a refresh / refresh-failure should trigger. */
export const setAuthCallbacks = (callbacks: {
  onTokensRefreshed: (token: string, refreshToken: string, userId: number) => void;
  onRefreshFailed: () => void;
}) => {
  onTokensRefreshed = callbacks.onTokensRefreshed;
  onRefreshFailed = callbacks.onRefreshFailed;
};

// Ensures only one refresh request is in flight at a time. Concurrent 401s all
// await the same promise rather than each firing their own refresh.
let refreshPromise: Promise<string | null> | null = null;

const runTokenRefresh = async (): Promise<string | null> => {
  if (!currentToken || !currentRefreshToken) {
    return null;
  }

  try {
    const { data } = await AXIOS_INSTANCE.post<LoginResponse>(
      '/api/Authentication/ValidateRefreshToken',
      { token: currentToken, refreshToken: currentRefreshToken }
    );

    if (data?.token && data?.refreshToken) {
      currentToken = data.token;
      currentRefreshToken = data.refreshToken;
      onTokensRefreshed?.(data.token, data.refreshToken, data.userId);
      return data.token;
    }

    return handleRefreshFailure();
  } catch {
    return handleRefreshFailure();
  }
};

/**
 * Clear the module's tokens *before* signing the user out. `onRefreshFailed`
 * (signout) fires a `/signout` request, and the request interceptor would
 * otherwise re-attach the stale token; if that call also 401s it would re-enter
 * refresh and loop until the React auth-state effect happens to null the tokens.
 * Nulling them here makes `shouldAttemptRefresh` false immediately, independent
 * of React state timing.
 */
const handleRefreshFailure = (): null => {
  currentToken = null;
  currentRefreshToken = null;
  onRefreshFailed?.();
  return null;
};

let installed = false;

/** Install the request (auth header) + response (refresh-on-401) interceptors. */
export const installAuthInterceptors = () => {
  if (installed) {
    return;
  }
  installed = true;

  AXIOS_INSTANCE.interceptors.request.use((config) => {
    if (currentToken) {
      config.headers.Authorization = `Bearer ${currentToken}`;
    }
    return config;
  });

  AXIOS_INSTANCE.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const original = error.config as
        | (InternalAxiosRequestConfig & { _retried?: boolean })
        | undefined;

      const shouldAttemptRefresh =
        error.response?.status === 401 &&
        !!original &&
        !original._retried &&
        // never try to refresh the refresh call itself
        !original.url?.includes('ValidateRefreshToken') &&
        !!currentToken &&
        !!currentRefreshToken;

      if (!shouldAttemptRefresh || !original) {
        return Promise.reject(error);
      }

      if (!refreshPromise) {
        refreshPromise = runTokenRefresh().finally(() => {
          refreshPromise = null;
        });
      }

      const newToken = await refreshPromise;
      if (!newToken) {
        return Promise.reject(error);
      }

      original._retried = true;
      original.headers.Authorization = `Bearer ${newToken}`;
      return AXIOS_INSTANCE(original);
    }
  );
};
